import React, {
	useContext,
	useEffect,
	useState,
	useRef,
	useCallback,
} from 'react';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import withAppContext from 'tbk-components/src/hooks/withAppContext';
import { useHashAsState } from 'tbk-components/src/hooks';
import ElasticMapper from 'tbk-components/src/services/ElasticMapper';
import qs from 'qs';
import Loading from 'tbk-components/src/components/Loading';
import cn from 'classnames';
import { IImage } from 'tbk-components/src/components/Image';
import NewsListingFilterBar from './NewsListingFilterBar';
import NewsListingTile from './NewsListingTile';
import NewsListingPagination from './NewsListingPagination';
import { debounce } from 'lodash';

export interface IBlogAuthor {
	name?: string;
	url?: string;
	image?: IImage;
}

export interface IBlogPostCategory {
	name?: string;
	url?: string;
}

export interface IBlogPostTag {
	name?: string;
	url?: string;
}

export interface IBlogPost {
	image?: IImage;
	heading?: string;
	author?: IBlogAuthor;
	readTime?: string;
	excerpt?: string;
	content?: string;
	url?: string;
	categories?: IBlogPostCategory[];
	tags?: IBlogPostTag[];
	date?: string;
}

export interface INewsListingProps extends IBaseElementProps {
	/**
	 * @noUI
	 */
	context?: any;
	/**
	 * @noUI
	 */
	assets?: any;
	/**
	 * Turn this on to show only a list of latest news and media items for this business unit.
	 */
	showOnlyLatestPosts?: boolean;
	/**
	 * Set the maximum number of posts to show.
	 */
	numberOfPostsToShow?: number;
	showPagination?: boolean;
	/**
	 * @noUI
	 */
	posts?: {
		total: number;
		hits: any[];
	};
	/**
	 * @noUI
	 */
	fetchArgs?: any;
	showFilterBar?: boolean;
	filterBarOnly?: boolean;
	filterSearchPlaceholder?: string;
	filterLabel?: string;
	displaySearchTaxonomies?: boolean;
	displaySearchPostTypes?: boolean;
	/**
	 * Text that shows if there are no results from filtering.
	 */
	noResultsMessage?: string;
	/**
	 * If SearchWP is active on the site, we need to use a different endpoint.  It needs to be
	 * a WP endpoint so SearchWP is fired up.  I set this serverside in the SearchWP controller
	 * in the calian theme.
	 * @noUI
	 */
	searchFunction?: 'getBlogRoll' | 'getBlogRollSearchWP';
}

/**
 * Show posts as a collection.
 *
 * @block
 * @icon media-document
 */
const NewsListing: React.FC<INewsListingProps> = withAppContext(
	({
		id,
		className,
		classNames = [],
		context,
		showOnlyLatestPosts = false,
		numberOfPostsToShow,
		showPagination = true,
		posts,
		fetchArgs,
		showFilterBar = true,
		filterBarOnly = false,
		filterLabel,
		displaySearchPostTypes = true,
		displaySearchTaxonomies = true,
		filterSearchPlaceholder,
		searchFunction = 'getBlogRoll',
		noResultsMessage = 'No results found.  Please adjust your filters',
	}) => {
		// @ts-ignore
		const { assets: contextAssets, Api } = useContext(context);
		const [assets, setAssets] = useState<any>(null);
		const [blogRoll, setBlogRoll] = useState<any>(null);
		const [termMap, setTermMap] = useState<any>(null);
		const [total, setTotal] = useState<number | null>(null);
		const [loading, setLoading] = useState<boolean>(false);

		// Format the taxonomy parameters for use by useHashAsState below.
		const taxonomy_parameters = ((taxonomies) => {
			const t: any = {};
			(taxonomies || []).forEach((tax: any) => {
				t[tax.taxonomy] = 'csv';
			});
			return t;
		})(assets?.taxonomies);

		// useHashAsState allows using window.location.hash as a state variable.
		const [state, hash, updateState, normalizeState] = useHashAsState({
			parameters: {
				page: 'int',
				...taxonomy_parameters,
				search: 'string',
			},
			defaults: {
				search: '',
				page: 1,
			},
		});

		// Ref to pass down to pagination so it can force a scroll
		// to top of blogRoll when user changes page.
		const topRef = useRef(null);

		// Fetch posts from the API, with a debounce to prevent multiple
		// API calls (e.g. on each keystroke when somebody is typing.)
		const fetchPostsFromApi = useCallback(
			debounce((args: any) => {
				const getBlogRoll = Api[searchFunction]
					? searchFunction
					: 'getBlogRoll';
				Api[searchFunction](args)
					.then((results: any) => {
						setTotal(results.total);
						setBlogRoll(ElasticMapper(results));
					})
					.finally(() => {
						setLoading(false);
					});
			}, 250),
			[searchFunction],
		);

		// Fetch new posts from the server when the search query changes.
		useEffect(() => {
			// We don't need to fetch posts if we're not showing them.
			if (filterBarOnly) {
				return;
			}
			// If a preset list of posts has been provided as a prop, display those and exit.
			if (posts) {
				setTotal(posts.total);
				setBlogRoll(ElasticMapper(posts));
				return;
			}
			// We can't display the filter bar if we don't have any assets.
			if (filterBarOnly && !assets) {
				return;
			}
			// Compile our Api call args from the hash query.
			let args = qs.parse(hash.replace(/^#!/, ''), {
				ignoreQueryPrefix: true,
			});
			args.posts_per_page = assets?.posts_per_page;
			if (fetchArgs) {
				args = {
					...fetchArgs,
					...args,
				};
			}
			if (numberOfPostsToShow) {
				args.posts_per_page = `${numberOfPostsToShow}`; // cast to string
			}
			setBlogRoll(null);
			setLoading(true);
			fetchPostsFromApi(args);
		}, [
			hash,
			assets,
			fetchArgs,
			posts,
			Api,
			showOnlyLatestPosts,
			numberOfPostsToShow,
		]);

		// Load in the assets used by the filter bar drop-downs (taxonomies, tags, etc.)
		useEffect(() => {
			const assetReqArgs = { ...fetchArgs };
			const setAssetsAndTermMap = (results: any) => {
				setAssets(results);
				const map: any = {};
				if (results.taxonomies) {
					results.taxonomies.forEach((taxonomy: any) => {
						taxonomy.terms.forEach((term: any) => {
							map[term.term_id] = term;
						});
					});
				}
				setTermMap(map);
			};
			if (contextAssets) {
				setAssetsAndTermMap(contextAssets);
			} else {
				Api.getBlogRollAssets(assetReqArgs).then((results: any) => {
					setAssetsAndTermMap(results);
				});
			}
		}, [contextAssets, Api, fetchArgs]);

		// Show a loading screen while we load assets.
		if (!assets) {
			return (
				<div className="loading-wrapper">
					<Loading />
				</div>
			);
		}

		// Show the filter only if we don't have a preset list of posts passed in,
		// or if that's all the user wants to display.
		const includeHeader = (showFilterBar && !posts) || filterBarOnly;

		return (
			<div
				id={id}
				className={
					className || cn('calian-news-listing', ...classNames)
				}
				ref={topRef}
			>
				{includeHeader && (
					<NewsListingFilterBar
						SearchPlaceholder={filterSearchPlaceholder}
						searchTaxonomies={displaySearchTaxonomies}
						searchPostTypes={displaySearchPostTypes}
						filterLabel={filterLabel}
						filters={state}
						assets={assets}
						onChange={updateState}
					/>
				)}
				{!filterBarOnly && (
					<div className="blogroll-results__container">
						<div className="blogroll-results__container-inner">
							{loading ? (
								<div className="loading-wrapper">
									<Loading />
								</div>
							) : (
								<>
									{total ? (
										<>
											<div
												className={cn(
													'blogroll-results__list',
													fetchArgs?.post_type &&
														`blogroll-results__list--${fetchArgs.post_type}`,
												)}
											>
												{(blogRoll || []).map(
													(blogroll: any) => (
														<NewsListingTile
															key={blogroll.ID}
															blogroll={blogroll}
															termMap={termMap}
														/>
													),
												)}
											</div>
											{showPagination && (
												<NewsListingPagination
													className="pagination-wrapper"
													page={state.page || 1}
													perPage={
														fetchArgs?.posts_per_page ||
														assets?.posts_per_page
													}
													total={total}
													onChange={(page: any) =>
														updateState({ page })
													}
													showAllLabel={false}
													topRef={topRef}
												/>
											)}
										</>
									) : (
										<p className="no-results mx-3">
											{noResultsMessage}
										</p>
									)}
								</>
							)}
						</div>
					</div>
				)}
			</div>
		);
	},
);

export default NewsListing;
